import React, { useState, useEffect } from 'react';
import { fmt } from '../utils/gst';
import { fmtDate } from '../utils/format';

const s = {
  page: { background: '#F5F6F8', minHeight: '100vh', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif', color: '#0D1B2E' },
  // Header
  header: { background: '#E8EEF8', borderBottom: '1px solid #C8D8F0', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, flexWrap: 'wrap' },
  logoWrap: { display: 'flex', alignItems: 'center', gap: 12 },
  logo: { height: 38, width: 'auto', maxWidth: 180, objectFit: 'contain' },
  studioBadge: { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#073E81', marginTop: 3 },
  headerRight: { textAlign: 'right' },
  docType: { fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#073E81', marginBottom: 4 },
  clientName: { fontSize: 18, fontWeight: 700, color: '#0D1B2E', lineHeight: 1.2 },
  clientSub: { fontSize: 12, color: '#5A6478', marginTop: 3 },
  // Summary band
  band: { background: '#073E81', padding: '14px 40px', display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' },
  stat: { display: 'flex', flexDirection: 'column', gap: 2 },
  statVal: { fontSize: 19, fontWeight: 800, color: '#fff', fontVariantNumeric: 'tabular-nums' },
  statLbl: { fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' },
  bandDiv: { width: 1, height: 32, background: 'rgba(255,255,255,0.2)' },
  // Main
  main: { padding: '28px 40px 48px', maxWidth: 1080, margin: '0 auto' },
  // Designer section
  designerHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 10, borderBottom: '2px solid #073E81' },
  avatar: { width: 30, height: 30, borderRadius: '50%', background: '#073E81', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, letterSpacing: '0.04em' },
  designerName: { fontSize: 14, fontWeight: 700 },
  designerCount: { marginLeft: 'auto', fontSize: 12, color: '#5A6478', fontVariantNumeric: 'tabular-nums' },
  designerAmt: { fontSize: 13, fontWeight: 700, color: '#073E81', fontVariantNumeric: 'tabular-nums' },
  // Entry card
  card: { background: '#fff', border: '1px solid #DDE1E9', borderRadius: 8, display: 'grid', gridTemplateColumns: '92px 1fr auto', marginBottom: 8, overflow: 'hidden', transition: 'box-shadow .15s' },
  // Flags a ₹0 line item — likely a missing price, not meant to be shared with the client as-is
  cardZero: { background: '#FDF0EE', borderColor: '#F5C6C0' },
  amtTotalZero: { color: '#C0392B' },
  imgCell: { width: 92, background: '#F5F6F8', borderRight: '1px solid #DDE1E9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 8px', flexShrink: 0 },
  thumb: { width: 76, height: 56, objectFit: 'cover', borderRadius: 5, border: '1px solid #DDE1E9', cursor: 'zoom-in', display: 'block' },
  placeholder: { width: 76, height: 56, borderRadius: 5, background: '#EEF0F4', border: '1.5px dashed #C0CBD8', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 },
  placeholderLbl: { fontSize: 8, color: '#9AA3B2', textAlign: 'center', lineHeight: 1.3 },
  body: { padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 5 },
  title: { fontSize: 13, fontWeight: 600, lineHeight: 1.3 },
  meta: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  tagDate: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#EEF0F4', color: '#5A6478' },
  tagCat: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#E8EEF8', color: '#073E81' },
  tagQty: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#E6F5ED', color: '#1E7E4E' },
  amtCell: { padding: '10px 14px 10px 10px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 3, flexShrink: 0, minWidth: 86 },
  amtTotal: { fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  amtRate: { fontSize: 11, fontWeight: 700, color: '#073E81', background: '#E8EEF8', borderRadius: 4, padding: '2px 7px', fontVariantNumeric: 'tabular-nums' },
  amtMeta: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 },
  amtGst: { fontSize: 10, fontWeight: 600, color: '#5A6478', fontVariantNumeric: 'tabular-nums' },
  amtCorr: { fontSize: 10, fontWeight: 700, color: '#C0392B', fontVariantNumeric: 'tabular-nums' },
  // Correction breakdown (same red accent used elsewhere in the app for corrections)
  corrBox: { background: '#FDF0EE', border: '1px solid #F5C6C0', borderRadius: 6, padding: '4px 8px', marginTop: 2 },
  corrHead: { color: '#C0392B', fontWeight: 700, fontSize: 10, display: 'block' },
  corrLine: { display: 'block', fontSize: 10, color: '#7A2020', marginTop: 2 },
  corrReason: { color: '#9A4040', fontWeight: 400 },
  // GST block
  gstWrap: { maxWidth: 320, marginLeft: 'auto', marginTop: 28, background: '#fff', border: '1px solid #DDE1E9', borderRadius: 8, overflow: 'hidden' },
  gstRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #DDE1E9', fontSize: 13, gap: 16 },
  gstLbl: { color: '#5A6478' },
  gstVal: { fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  gstTotal: { display: 'flex', justifyContent: 'space-between', padding: '13px 16px', background: '#073E81', fontSize: 14, gap: 16 },
  gstTotalLbl: { color: '#fff', fontWeight: 700 },
  gstTotalVal: { color: '#fff', fontWeight: 800, fontSize: 17, fontVariantNumeric: 'tabular-nums' },
  // Footer
  footer: { padding: '16px 40px', borderTop: '1px solid #DDE1E9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#9AA3B2', flexWrap: 'wrap', gap: 8 },
};

function initials(name) {
  return name.split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase();
}

// Presentational, data-driven render of the pricing "work report" — shared by WorkReportPage
// (token-driven, public) and the owner's in-app "View" preview (ReportPage.jsx, authenticated).
export default function WorkReportView({ data }) {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const h = e => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [lightbox]);

  const { month, fromMonth, toMonth, business, client, designers = [], totals } = data || {};
  const fmtMonth = m => new Date(m + '-01').toLocaleString('default', { month: 'long', year: 'numeric' });
  const monthLabel = fromMonth && toMonth ? `${fmtMonth(fromMonth)} – ${fmtMonth(toMonth)}` : month ? fmtMonth(month) : '';
  const totalDesigns = designers.reduce((s, d) => s + d.designCount, 0);
  const totalItems = designers.reduce((s, d) => s + d.entries.length, 0);

  return (
    <div style={s.page}>
      {/* Below ~480px the fixed 92px-image + auto-width amount grid columns don't leave the
          amount enough room next to a long title, so it gets clipped at the screen edge —
          reflow the amount onto its own full-width row instead. */}
      <style>{`
        @media (max-width: 480px) {
          .wr-header, .wr-band, .wr-main, .wr-footer { padding-left: 16px !important; padding-right: 16px !important; }
          .wr-card { grid-template-columns: 92px 1fr !important; }
          .wr-amt {
            grid-column: 1 / -1 !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: space-between !important;
            border-top: 1px solid #DDE1E9;
            margin-top: 4px;
            padding: 8px 12px !important;
            min-width: 0 !important;
          }
        }
      `}</style>
      {/* Header */}
      <div className="wr-header" style={s.header}>
        <div style={s.logoWrap}>
          <img src="/8d-logo.svg" alt="8D Studios" style={s.logo} />
        </div>
        <div style={s.headerRight}>
          <div style={s.docType}>Work Report</div>
          <div style={s.clientName}>{client?.name}</div>
          <div style={s.clientSub}>{monthLabel}</div>
        </div>
      </div>

      {/* Summary band */}
      <div className="wr-band" style={s.band}>
        <div style={s.stat}>
          <div style={s.statVal}>{totalDesigns}</div>
          <div style={s.statLbl}>Total Designs</div>
        </div>
        <div style={s.bandDiv} />
        <div style={s.stat}>
          <div style={s.statVal}>{totalItems}</div>
          <div style={s.statLbl}>Line Items</div>
        </div>
        <div style={s.bandDiv} />
        <div style={s.stat}>
          <div style={s.statVal}>{fmt(totals?.subtotal || 0)}</div>
          <div style={s.statLbl}>Sub-Total</div>
        </div>
        <div style={s.bandDiv} />
        <div style={s.stat}>
          <div style={s.statVal}>{fmt(totals?.grandTotal || 0)}</div>
          <div style={s.statLbl}>Total with GST</div>
        </div>
        <div style={s.bandDiv} />
        <div style={s.stat}>
          <div style={s.statVal}>{designers.length}</div>
          <div style={s.statLbl}>Designers</div>
        </div>
      </div>

      {/* Main content */}
      <div className="wr-main" style={s.main}>
        {designers.map(designer => (
          <div key={designer.name} style={{ marginBottom: 36 }}>
            {/* Designer header */}
            <div style={s.designerHead}>
              <div style={s.avatar}>{initials(designer.name)}</div>
              <div style={s.designerName}>{designer.name}</div>
              <div style={s.designerCount}>{designer.entries.length} items · {designer.designCount} designs</div>
              <div style={s.designerAmt}>{fmt(designer.subtotal)}</div>
            </div>

            {/* Entry cards */}
            {designer.entries.map(entry => {
            const isZero = parseFloat(entry.total) === 0;
            return (
              <div key={entry.id} className="wr-card" style={isZero ? { ...s.card, ...s.cardZero } : s.card}>
                {/* Image */}
                <div style={s.imgCell}>
                  {entry.proof_image
                    ? <img
                        src={`/uploads/proofs/${entry.proof_image}`}
                        alt={entry.description}
                        style={s.thumb}
                        onClick={() => setLightbox(`/uploads/proofs/${entry.proof_image}`)}
                      />
                    : <div style={s.placeholder}>
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" style={{ color: '#C0CBD8' }}>
                          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
                          <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                          <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        <div style={s.placeholderLbl}>Image<br />coming</div>
                      </div>
                  }
                </div>
                {/* Body */}
                <div style={s.body}>
                  <div style={s.title}>{entry.description}</div>
                  <div style={s.meta}>
                    <span style={s.tagDate}>{fmtDate(entry.work_date)}</span>
                    <span style={s.tagCat}>{entry.category_name}</span>
                    {entry.quantity > 1 && <span style={s.tagQty}>×{entry.quantity}</span>}
                  </div>
                  {entry.correction_charge > 0 && (
                    <div style={s.corrBox}>
                      <span style={s.corrHead}>+{entry.corrections} correction{entry.corrections > 1 ? 's' : ''}</span>
                      {entry.correction_lines.map((l, i) => {
                        const base = parseFloat(l.price || 0) * l.count;
                        const gst = base * parseFloat(l.gst_percent || 0) / 100;
                        return (
                          <span key={i} style={s.corrLine}>
                            {l.type_name} ×{l.count}: {fmt(base)} + {l.gst_percent}% GST = <strong>{fmt(base + gst)}</strong>
                            {l.reason && <span style={s.corrReason}> ({l.reason})</span>}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Amount */}
                <div className="wr-amt" style={s.amtCell}>
                  <div style={isZero ? { ...s.amtTotal, ...s.amtTotalZero } : s.amtTotal}>{fmt(entry.total)}</div>
                  <div style={s.amtMeta}>
                    <div style={s.amtRate}>{fmt(entry.unit_price)} × {entry.quantity}</div>
                    {entry.gst_amount > 0 && <div style={s.amtGst}>+{fmt(entry.gst_amount)} GST ({entry.gst_percent}%)</div>}
                    {entry.correction_charge > 0 && <div style={s.amtCorr}>+{fmt(entry.correction_charge)} corr.</div>}
                  </div>
                </div>
              </div>
            );})}
          </div>
        ))}

        {/* GST block */}
        {totals && (
          <div style={s.gstWrap}>
            <div style={s.gstRow}>
              <span style={s.gstLbl}>Sub-Total ({totalItems} items)</span>
              <span style={s.gstVal}>{fmt(totals.subtotal)}</span>
            </div>
            {totals.taxType === 'intrastate' ? <>
              <div style={s.gstRow}>
                <span style={s.gstLbl}>CGST @ 9%</span>
                <span style={s.gstVal}>{fmt(totals.cgst)}</span>
              </div>
              <div style={s.gstRow}>
                <span style={s.gstLbl}>SGST @ 9%</span>
                <span style={s.gstVal}>{fmt(totals.sgst)}</span>
              </div>
            </> : (
              <div style={s.gstRow}>
                <span style={s.gstLbl}>IGST @ 18%</span>
                <span style={s.gstVal}>{fmt(totals.igst)}</span>
              </div>
            )}
            <div style={s.gstTotal}>
              <span style={s.gstTotalLbl}>Total Amount</span>
              <span style={s.gstTotalVal}>{fmt(totals.grandTotal)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="wr-footer" style={s.footer}>
        <span>Generated by <strong style={{ color: '#0D1B2E' }}>8D Studios Business OS™</strong> · {business?.name}</span>
        <span>Work report · Pricing included · {monthLabel}</span>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, cursor: 'zoom-out' }}>
          <img src={lightbox} alt="proof" onClick={e => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.5)', cursor: 'default' }} />
          <button onClick={() => setLightbox(null)}
            style={{ position: 'absolute', top: 16, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 18, borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
